import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { fetchInitialAlertAdr } from '@rsos/components/capstone/DataCards/adrDataSlice';
import {
  hasNotificationPermission,
  pushNotification,
} from '@rsos/components/capstone/PushNotifications/PushNotifications';
import rsosIcon from '@rsos/components/capstone/PushNotifications/RSOS-Brandmark.png';
import { ALERTS } from '@rsos/constants/incidentTypes';
import { gspx } from '@rsos/gainsight';
import {
  setChatSelectedAlertID,
  fetchAlertConversationStatus,
} from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import {
  resetIDWithNewAlert,
  fetchAlert,
} from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { fetchAgencyLinks as fetchAlertsRoutingAgencyLinks } from '@rsos/reduxDataStore/src/features/alertsRouting/alertsRoutingSlice';
import { setSelectedCallerID } from '@rsos/reduxDataStore/src/features/emergencyCalls/emergencyCallsSlice';
import { sortedAlertsSelector } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import isAlertsNotificationEnabled from '@rsos/utils/isAlertsNotificationEnabled';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePrevious from '@rsos/utils/usePrevious';
import Loader from '../../Loader';
import Alert from '../Alert';
import NoResults from '../NoResults';
import {
  LoaderWrapper,
  NoResultsWrapper,
  ScrollableList,
} from './AlertsQueue.styles';
import { trackClickAlert } from './alertQueueTrackings';

const NOTIFICATION_ALERT_TITLE = 'New Alert from RapidSOS';
const NOTIFICATION_ACTIVE_ASSAILANT_ALERT_TITLE =
  'RapidSOS Active Assailant Alert';
const ACTIVE_ASSAILANT = 'ACTIVE_ASSAILANT';

/**
 *
 * @param {object} alerts - list of alerts/incidents
 * @param {object} currentRole - current role object includes application name
 * @param {boolean} isLoading - is alerts loading?
 * @param {boolean} isVisible - is the current queue visible?
 * @param {Function} onQueueHover - Hover effect handler for markers
 * @param {Function} onQueueItemClicked - Function to handle click on a queue item for RSP
 * @param {string} selectedAlertID = the selected alert id
 * @param {string} selectedCallerID = the selected call id
 * @param {boolean} setShowPane - display the detail pane?
 * @param {String} userID - ID of current user
 * @param {Class<Hermes>} WSInstance - Hermes websocket instance
 * @param {Class<StreamWS>} streamWS - StreamWS websocket instance
 *
 */
const AlertsQueue = ({
  alerts,
  currentRole,
  isLoading,
  isVisible,
  selectedAlertID,
  setActiveQueueTab,
  setShowPane,
  userProfile,
  WSInstance,
  streamWS,
  irpAlertsID,
  onQueueHover = () => {},
  onQueueItemClicked = () => {},
  inactiveIncidentID,
  activeIncidentID,
}) => {
  const dispatch = useDispatch();
  const previousSelectedAlertID = usePrevious(selectedAlertID);
  const { isIRPresponsiveConditional, irpVersion } = useIsIRP();
  const newAlertID = useSelector(state => state.incidents.IDWithNewAlert);
  const hoveredAlertID = useSelector(
    state => state.hoveredIncident?.hoveredAlertID,
  );
  const psaps = useSelector(state => state.psaps);
  const currentApplication = useSelector(
    state => state.sinatra.user.currentRole.application,
  );
  const { alertsQueueOrder, latestMsgTime } = useSelector(
    state => state.incidents,
  );

  const sortedAlerts = sortedAlertsSelector({
    alerts,
    alertsQueueOrder,
    isCentralStation: false,
  });

  const currentOrgName = useSelector(
    state => state.sinatra.user.currentOrg.name,
  );
  const [initialLoad, setInitialLoad] = useState(true);

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latestMsgTime]);

  useEffect(() => {
    const newAlert = alerts[newAlertID];
    const isSupplementalOnly = newAlert?.supplemental_only;
    if (
      newAlert?.location_history?.length === 1 &&
      !isSupplementalOnly &&
      hasNotificationPermission(irpVersion)
    ) {
      const options = {
        icon: rsosIcon,
      };

      const handleClickNewAlertNotification = () => {
        setActiveQueueTab(ALERTS);
        handleSelected(
          newAlertID,
          isSupplementalOnly,
          newAlert.status.name,
          newAlert.isUnread,
        );
      };
      pushNotification({
        title:
          newAlert?.emergency_type?.name === ACTIVE_ASSAILANT
            ? NOTIFICATION_ACTIVE_ASSAILANT_ALERT_TITLE
            : NOTIFICATION_ALERT_TITLE,
        options,
        callback: () => {
          window.focus();
          handleClickNewAlertNotification();
        },
      });
    }
    if (activeIncidentID && !inactiveIncidentID) {
      dispatch(resetIDWithNewAlert());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newAlertID]);

  useEffect(() => {
    if (irpAlertsID) {
      const alertID = irpAlertsID.split('alert://')[1];
      if (alertID) {
        handleSelected(alertID);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [irpAlertsID]);

  function handleSelected(id, isSupplementalOnly, alertStatus, isUnread) {
    if (id === newAlertID) {
      dispatch(resetIDWithNewAlert());
    }
    const { application: appName } = currentRole;
    setShowPane(true);
    onQueueItemClicked();
    if (previousSelectedAlertID) {
      WSInstance && WSInstance.onUnSubscribeAlertID(previousSelectedAlertID);
      streamWS && streamWS.onUnSubscribeAlertID(previousSelectedAlertID);
    }
    WSInstance && WSInstance.onSubscribeAlertID(id);
    streamWS && streamWS.onSubscribeAlertID(id);
    dispatch(
      fetchAlert({
        currentApplication,
        currentOrgName,
        alertID: id,
        shouldSetID: true,
      }),
    ).then(() => {
      dispatch(setChatSelectedAlertID(id));
      dispatch(setSelectedCallerID(null));
      dispatch(
        fetchAlertConversationStatus({
          orgName: currentOrgName,
          conversationID: `chat-portal-default-${id.split('alert-')[1]}`,
        }),
      );
      dispatch(fetchInitialAlertAdr(id));
    });

    gspx.trackCustomEvent('Data Jurisdiction', {
      name: `Clicks on specific alert in ${appName}`,
      Category: 'Jurisdiction View',
      'Launched date': new Date(),
      AlertID: id,
    });

    trackClickAlert(alertStatus, appName, isSupplementalOnly, isUnread, id);
  }

  useEffect(() => {
    if (isAlertsNotificationEnabled(psaps)) {
      dispatch(fetchAlertsRoutingAgencyLinks(currentOrgName));
    }
    // Handle selectedAlertID on page refresh
    if (selectedAlertID && alerts[selectedAlertID]) {
      handleSelected(
        selectedAlertID,
        alerts[selectedAlertID].supplemental_only,
        alerts[selectedAlertID].status.name,
        alerts[selectedAlertID].isUnread,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isApplyResponsiveRules = !isIRPresponsiveConditional;
  return (
    isVisible && (
      <>
        {isLoading ? (
          <LoaderWrapper>
            <Loader />
          </LoaderWrapper>
        ) : Object.keys(alerts).length ? (
          <ScrollableList isApplyResponsiveRules={isApplyResponsiveRules}>
            {sortedAlerts.map((item, index) => {
              return (
                <div
                  key={index}
                  onClick={() =>
                    handleSelected(
                      item.alert_id,
                      item.supplemental_only,
                      item.status.name,
                      item.isUnread,
                    )
                  }
                  onMouseEnter={() =>
                    onQueueHover(item.alert_id, 'queue', ALERTS)
                  }
                  onMouseLeave={() => onQueueHover(null, 'queue', ALERTS)}
                >
                  <Alert
                    alert={item}
                    isMapMarkerHovered={hoveredAlertID === item.alert_id}
                    isSelected={item.alert_id === selectedAlertID}
                    userProfile={userProfile}
                  />
                </div>
              );
            })}
          </ScrollableList>
        ) : (
          <NoResultsWrapper>
            <NoResults title="No Alerts" description="No alerts were found." />
          </NoResultsWrapper>
        )}
      </>
    )
  );
};

AlertsQueue.propTypes = {
  alerts: PropTypes.object.isRequired,
  currentRole: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
  isVisible: PropTypes.bool.isRequired,
  selectedAlertID: PropTypes.string,
  setShowPane: PropTypes.func,
  userProfile: PropTypes.object,
  WSInstance: PropTypes.object,
  streamWS: PropTypes.object,
  irpAlertsID: PropTypes.string,
  onQueueHover: PropTypes.func,
  onQueueItemClicked: PropTypes.func,
};

export default AlertsQueue;
